import type { Location } from 'react-router-dom'

/**
 * What a clicked row lets the sheet draw before `/event/<id>` answers.
 *
 * The title, the picture and the night line, and nothing else. Every one of them is the
 * row's field exactly as the server sent it — never what the surface printed, which is
 * shortened on a chip and split into headline and support on a card. Anything that needs
 * `/event` — the marks, the tickets, the blurb, the bill — is not here, because the row's
 * version of it is a different fact: a stranger's calendar row carries `liked` as the
 * editorial fallback, and a row's `picked` has a half the sheet's does not.
 *
 * `night` is null for a row that does not carry the server's London strings. A front-page
 * row has only `startsAt`, and formatting that here would be a second timezone rule; the
 * line arrives with `/event` instead.
 */
export interface Seed {
  id: string
  title: string
  image: string | null
  night: {
    dayLong: string
    dayRange: string | null
    time: string
    venue: string
    venueSlug: string | null
  } | null
}

/** The fields a row needs to seed the sheet. A calendar row has all of them; a front-page
 *  row has no `dayLong` or `time`, and seeds no night line. */
export interface SeedRow {
  id: string
  title: string
  image: string | null
  venue: string
  venueSlug: string | null
  dayRange?: string | null
  dayLong?: string
  time?: string
}

/**
 * Which page load a seed was made in.
 *
 * A seed travels in the navigation's state, and a browser keeps `history.state` across a
 * reload — so without this a reloaded `/e/<id>` would draw a row from before the reload,
 * which could be hours old, and a reload would stop being the cold open it is everywhere
 * else. Module state is what a reload does discard, so a seed stamped with this page load's
 * mark is only ever read back by the same page load.
 */
const LOAD = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`

function seedOf(row: SeedRow): Seed {
  return {
    id: row.id,
    title: row.title,
    image: row.image,
    night:
      typeof row.dayLong === 'string'
        ? {
            dayLong: row.dayLong,
            dayRange: row.dayRange ?? null,
            time: row.time ?? '',
            venue: row.venue,
            venueSlug: row.venueSlug,
          }
        : null,
  }
}

/**
 * The navigation state for opening a gig over `here`: the page underneath, and the row's
 * seed when a row was clicked.
 *
 * Only the seed's fields are copied in, not the row, because the state is cloned into the
 * history entry and the row carries the friends, the marks and the reasons.
 */
export function opening(here: Location, row?: SeedRow): { background: Location; seed?: unknown } {
  return row ? { background: here, seed: { ...seedOf(row), load: LOAD } } : { background: here }
}

/**
 * The seed in a location's state, if it was made in this page load for this id.
 *
 * The id is checked because the state belongs to the history entry, and the sheet's id is
 * the address: a seed for one gig must never be drawn under another's.
 */
export function seedIn(state: unknown, id: string): Seed | null {
  const seed = (state as { seed?: unknown } | null)?.seed as (Seed & { load?: unknown }) | undefined
  if (!seed || typeof seed !== 'object' || seed.load !== LOAD || seed.id !== id) return null
  return { id: seed.id, title: seed.title, image: seed.image, night: seed.night }
}
